import { z } from 'zod';
import { ROLES, GROUPS, ACTIVITY_FIELDS } from './constants.js';
export const roleSchema = z.enum(ROLES);
export const groupSchema = z.enum(GROUPS);
export const loginSchema = z.object({
    pin: z.string().trim().min(1, 'PIN required').max(64),
});
export const callAttemptSchema = z.object({
    date: z.string().default(''),
    outcome: z.string().default(''),
    note: z.string().optional(),
    emailedToo: z.boolean().optional(),
});
/** Structured lead-qualification capture (persisted in Lead.qualification). */
export const qualificationSchema = z.object({
    verifyContact: z.string().default(''),
    title: z.string().default(''),
    providers: z.string().default(''),
    specialtyType: z.string().default(''),
    systemsUsed: z.string().default(''),
    currentBiller: z.string().default(''),
    otherDecisionMakers: z.string().default(''),
    leadInterest: z.string().default(''),
    painPoints: z.array(z.string()).default([]),
    timeline: z.string().default(''),
    budget: z.string().default(''),
    discoveryAt: z.string().default(''),
    additionalNotes: z.string().default(''),
});
/** Fields a client may write on a lead. All optional (PATCH semantics). */
export const leadWritableSchema = z.object({
    company: z.string().optional(),
    physicians: z.number().int().nonnegative().optional(),
    specialty: z.string().optional(),
    first: z.string().optional(),
    last: z.string().optional(),
    title: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    website: z.string().optional(),
    linkedin: z.string().optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    campaign: z.string().optional(),
    campaignStart: z.string().optional(),
    calls: z.array(callAttemptSchema).max(6).optional(),
    notes: z.array(z.string()).max(6).optional(),
    leadDate: z.string().optional(),
    qualReason: z.string().optional(),
    vendorPM: z.string().optional(),
    vendorEHR: z.string().optional(),
    vendorLIS: z.string().optional(),
    billing: z.string().optional(),
    practiceType: z.string().optional(),
    isCgm: z.boolean().optional(),
    status: z.string().optional(),
    readiness: z.number().int().min(0).max(100).optional(),
    qualification: qualificationSchema.partial().optional(),
    removedFromQueue: z.boolean().optional(),
    lastTouch: z.string().nullable().optional(),
    followUp: z.string().nullable().optional(),
    source: z.string().optional(),
    repId: z.string().nullable().optional(),
});
/** Log a one-click disposition against a lead (Phase 1 call panel). */
export const dispositionSchema = z.object({
    code: z.string().min(1),
    note: z.string().optional(),
    emailedToo: z.boolean().optional(),
    followUp: z.string().nullable().optional(),
});
/** Submit the qualification form: persist fields + recompute the authoritative readiness. */
export const qualifySubmitSchema = qualificationSchema.partial().extend({
    meetingSet: z.boolean().optional(),
});
/** Merge a duplicate group into a master record (Phase 6). `fieldSources` optionally overrides
 *  individual fields to come from a specific record (field-level merge). */
export const mergeSchema = z.object({
    masterId: z.string().min(1),
    mergeIds: z.array(z.string().min(1)).min(1),
    fieldSources: z.record(z.string(), z.string()).optional(),
});
export const duplicateDismissSchema = z.object({
    key: z.string().min(1),
});
/** Pre-create duplicate check: any of email / phone / company+last identifies a possible match. */
export const duplicateCheckSchema = z.object({
    email: z.string().optional(),
    phone: z.string().optional(),
    company: z.string().optional(),
    last: z.string().optional(),
});
export const leadCreateSchema = leadWritableSchema.extend({
    company: z.string().min(1, 'Company name required'),
    leadRef: z.string().optional(),
});
export const leadsQuerySchema = z.object({
    rep: z.string().optional(),
    campaign: z.string().optional(),
    specialty: z.string().optional(),
    state: z.string().optional(),
    status: z.string().optional(),
    source: z.string().optional(),
    q: z.string().optional(),
    /** Time-zone bucket filter (ET/CT/MT/PT/AK/HI). */
    tz: z.string().optional(),
    /** Quick views: callable-now, follow-ups due, untouched-new, SLA-risk (7d+), my-SQLs. */
    callable: z.coerce.boolean().optional(),
    followUpsDue: z.coerce.boolean().optional(),
    untouched: z.coerce.boolean().optional(),
    slaRisk: z.coerce.boolean().optional(),
    mySqls: z.coerce.boolean().optional(),
    /** Aging bucket by last-touch age: fresh (<7d), aging (7–30d), stale (30d+). */
    aging: z.enum(['fresh', 'aging', 'stale']).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(25),
    sort: z.string().default('leadDate'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    includeTotal: z.coerce.boolean().default(true),
});
export const bulkReassignSchema = z.object({
    leadIds: z.array(z.string()).min(1),
    repId: z.string().min(1),
});
export const activityUpsertSchema = z.object({
    rep: z.string().optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}/, 'date must be YYYY-MM-DD'),
    fields: z
        .object(Object.fromEntries(ACTIVITY_FIELDS.map((f) => [f, z.coerce.number().int().nonnegative().default(0)])))
        .partial()
        .extend({
        submitted: z.boolean().optional(),
        connects: z.coerce.number().int().nonnegative().optional(),
        // Time spent (hours, decimals allowed).
        mtgHrs: z.coerce.number().nonnegative().optional(),
        podHrs: z.coerce.number().nonnegative().optional(),
        liHrs: z.coerce.number().nonnegative().optional(),
        oneOnOneHrs: z.coerce.number().nonnegative().optional(),
        otherHrs: z.coerce.number().nonnegative().optional(),
    }),
});
/** The time-spent (hours) fields on the daily activity form. */
export const ACTIVITY_TIME_FIELDS = ['mtgHrs', 'podHrs', 'oneOnOneHrs', 'otherHrs'];
export const ACTIVITY_TIME_LABELS = {
    mtgHrs: 'Team Meetings',
    podHrs: 'Pod Meetings',
    oneOnOneHrs: '1:1',
    otherHrs: 'Other',
};
export const userCreateSchema = z.object({
    name: z.string().min(1),
    repKey: z
        .string()
        .min(1)
        .regex(/^[a-z0-9_-]+$/, 'repKey must be lowercase letters/numbers/-/_'),
    role: roleSchema,
    group: groupSchema.default('CGM'),
    region: z.string().optional(),
    active: z.boolean().default(true),
});
export const userUpdateSchema = z.object({
    name: z.string().min(1).optional(),
    role: roleSchema.optional(),
    group: groupSchema.optional(),
    region: z.string().nullable().optional(),
    active: z.boolean().optional(),
    regeneratePin: z.boolean().optional(),
    regenerateToken: z.boolean().optional(),
});
export const importSchema = z.object({
    campaign: z.string().optional(),
    source: z.string().optional(),
    date: z.string().optional(),
    assignment: z
        .object({
        mode: z.enum(['even', 'byRep', 'unassigned', 'toRep']).default('even'),
        repIds: z.array(z.string()).optional(),
        // Target rep for `toRep` mode: every imported lead is assigned to this rep.
        repId: z.string().optional(),
    })
        .optional(),
    rows: z.array(z.record(z.string(), z.any())).max(20000),
    // Index of this batch's first row within the whole import. Lets the client
    // send large imports in chunks while "even" assignment stays balanced across
    // batches (round-robin continues from where the previous batch left off).
    offset: z.number().int().min(0).optional(),
    // Drop rows that duplicate a lead already in the hub (matched by email, phone
    // digits, or company + last name) instead of importing them.
    skipDuplicates: z.boolean().optional(),
});
export const metaPutSchema = z.object({
    meta: z.union([z.string(), z.record(z.string(), z.any())]),
});
const weight = z.coerce.number().min(0).max(1);
export const leaderboardSettingsSchema = z.object({
    weights: z
        .object({ opps: weight, conv: weight, obql: weight, activity: weight })
        .partial()
        .optional(),
    thresholds: z
        .object({
        highActivityPct: z.coerce.number(),
        lowActivityPct: z.coerce.number(),
        highConvPct: z.coerce.number(),
        lowConvPct: z.coerce.number(),
        decliningTrend: z.coerce.number(),
        strongGrowth: z.coerce.number(),
    })
        .partial()
        .optional(),
    repLabels: z.record(z.string(), z.string()).optional(),
    notes: z.string().max(20000).optional(),
    coachingNotes: z.string().max(20000).optional(),
});
//# sourceMappingURL=schemas.js.map